import { CodeSandboxOutlined, ReloadOutlined, StarOutlined } from '@ant-design/icons';
import {
  Button,
  ButtonProps,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  HostFieldsFieldsFragment,
  MeetingFieldsFragment,
  useResetMeetingMutation,
  useUpdateMeetingMutation,
} from '@/@generated/codegen/API';
import { getMeetingStatus, MEETING_STATUS } from '@/features/meeting/helpers/getMeetingStatus';
import ConfirmModal from '@/ui/ConfirmModal/ConfirmModal';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { NPSConfirmationModal } from './NPSConfirmationModal/NPSConfirmationModal';

type Props = {
  meeting: MeetingFieldsFragment;
  hostFields: HostFieldsFieldsFragment;
  buttonProps?: ButtonProps;
};

export default function LiveMeetingInteractionsMenu({ meeting, buttonProps, hostFields }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const npsDisclosure = useDisclosure();
  const restartDisclosure = useDisclosure();
  const meetingStatus = getMeetingStatus(meeting);

  const sandboxModeEnabled = [
    MEETING_STATUS.IDLE,
    MEETING_STATUS.READY,
    MEETING_STATUS.TESTING,
  ].includes(meetingStatus);

  const sendNPSEnabled = [
    MEETING_STATUS.DEMO,
    MEETING_STATUS.STARTED,
    MEETING_STATUS.TESTING,
  ].includes(meetingStatus);

  const restartMeetingEnabled = [
    MEETING_STATUS.READY, // actually we also want to be able to restart meetings in the READY state (it's the same as start meeting)
    MEETING_STATUS.STARTED,
    MEETING_STATUS.DEMO,
    MEETING_STATUS.TESTING,
  ].includes(meetingStatus);

  const [mutationResetMeeting] = useResetMeetingMutation();
  const [mutationUpdateMeeting] = useUpdateMeetingMutation();

  const handleMeetingRestart = async (meetingID: string) => {
    await mutationResetMeeting({
      variables: {
        where: {
          id: meetingID,
        },
        data: {
          setStartDate: meetingStatus !== MEETING_STATUS.TESTING,
        },
      },
    });
  };

  const handleSandboxChange = async (meetingID: string, newValue: boolean) => {
    await mutationUpdateMeeting({
      variables: {
        data: {
          id: meetingID,
          isInTesting: newValue,
        },
      },
    });
    // NOTE: If we are disabling sandbox mode, we also want to clean the meeting data
    if (!newValue) {
      await mutationResetMeeting({
        variables: {
          where: {
            id: meetingID,
          },
          data: {
            setStartDate: false,
          },
        },
      });
    }
  };

  if (meeting.isArchived) return null;
  return (
    <>
      <NPSConfirmationModal meeting={meeting} hostFields={hostFields} disclosure={npsDisclosure} />
      <ConfirmModal
        title={
          <div>
            {t('admin.home.table.restartMeeting.button.confirmText')}
            <br />
            {t('meeting.header.confirmRestart')}
          </div>
        }
        onConfirm={() => {
          handleMeetingRestart(meeting.id)
            .then(() =>
              toast({
                title: 'Meeting restarted',
                status: 'success',
                duration: 5000,
                isClosable: true,
              })
            )
            .catch((err) => {
              captureSentry({
                title: 'Error restarting meeting',
                error: err,
                detail: {
                  meetingID: meeting.id,
                },
              });
              toast({
                title: 'Error restarting meeting',
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
            });
        }}
        disclosure={restartDisclosure}
      />
      <Menu>
        <MenuButton as={Button} variant="secondary" size="xs" {...buttonProps}>
          {t('liveMeetingInteractions.btn.title')}
        </MenuButton>
        <MenuList>
          <MenuItem
            closeOnSelect={false}
            icon={<CodeSandboxOutlined />}
            isDisabled={!sandboxModeEnabled}
            onClick={() => {
              handleSandboxChange(meeting.id, !meeting.isInTesting)
                .then(() => {
                  toast({
                    title: 'SandBox mode changed',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                  });
                })
                .catch((err) => {
                  captureSentry({
                    title: 'Error changing sandbox mode',
                    error: err,
                    detail: {
                      meetingID: meeting.id,
                    },
                  });
                  toast({
                    title: 'Error changing sandbox mode',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                  });
                });
            }}
          >
            <HStack width="100%" justifyContent="space-between">
              <Text>{t('liveMeetingInteractions.btn.test.label')}</Text>
              <Switch variant="blue" isChecked={!!meeting.isInTesting}></Switch>
            </HStack>
          </MenuItem>
          <MenuItem
            icon={<StarOutlined />}
            onClick={npsDisclosure.onOpen}
            isDisabled={!sendNPSEnabled}
          >
            {t('liveMeetingInteractions.btn.nps.label')}
          </MenuItem>
          <MenuItem
            icon={<ReloadOutlined />}
            onClick={restartDisclosure.onOpen}
            isDisabled={!restartMeetingEnabled}
          >
            {t('liveMeetingInteractions.btn.restart.label')}
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
}
